import React, { useState, useEffect } from "react";
import {
  Button,
  Container,
  Grid,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  Box,
  Typography,
  InputBase,
  IconButton,
  Pagination,
  TablePagination,
  MenuItem,
  Select,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";
import html2pdf from "html2pdf.js";
import iPaisaLogo from "../Assets/iPaisaLogo/iPaisaLogo.jpg";
import { DownloadIcon, PartnerArrowIcon } from "../../res/NewWebIcons";
import DownloadIcon1 from "@mui/icons-material/Download";
import PayinReportInvoice from "./PayinReportInvoice";
import { CSVLink } from "react-csv";

const useStyles = makeStyles(() => ({
  evenRow: {
    backgroundColor: "#D0EFFF",
  },
  oddRow: {
    backgroundColor: "#FFFFFF",
  },
}));

const Utility = () => {
  const [payInData, setPayInData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [statusCodeValue, setStatusCodeValue] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const classes = useStyles();

  const handleDownloadInvoice = async (data) => {
    const receiptElement = document.getElementById(`receipt-${data.txnid}`);

    if (!receiptElement) {
      console.error("Receipt element not found:", `receipt-${data.txnid}`);
      return;
    }

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Payin Receipt-${data.txnid}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: {
        unit: "in",
        orientation: "landscape",
        format: "a4",
        margin: 0.5,
      },
    };

    try {
      await html2pdf().from(receiptElement).set(opt).save();
    } catch (error) {
      console.error("Error generating PDF: ", error);
    }
  };

  const handleDownloadCSV = () => {
    const csvData = filteredData.map((row, index) => ({
      SrNo: index + 1,
      TxnID: row.txnid,
      UTRNo: row.easepayid,
      Date: formatDate(row.addedon),
      PayInAmount: row.netAmountDebit || "N/A",
      Amount: row.amount,
      Status: row.status === "success" ? "Successful" : "Failed",
    }));
    return csvData;
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text("Pay In Report", 14, 10);
    doc.autoTable({
      head: [["Sr. No.", "Txn ID", "UTR No", "Date", "Amount", "Status"]],
      body: filteredData.map((row, index) => [
        index + 1,
        row.externalRef,
        row.easepayid,
        formatDate(row.timestamp),
        row.amount || "N/A",
        row.status === "success" ? "Successful" : "Failed",
      ]),
    });
    doc.save("pay_in_report.pdf");
  };

  const handleSearch = () => {
    setCurrentPage(0);
    if (!searchQuery.trim()) {
      fetchCompleteData(0, fromDate, toDate, "", 10);
    } else {
      fetchCompleteData(0, fromDate, toDate, searchQuery, pageSize);
    }
  };

  const handleSearchInput = (event) => {
    const updatedSearchQuery = event.target.value;
    setSearchQuery(updatedSearchQuery);
    if (updatedSearchQuery.length === 0) {
      fetchCompleteData(0, "", "", "", pageSize);
    }
  };

  const fetchCompleteData = async (
    page,
    fromDate,
    toDate,
    searchQuery,
    pageSize
  ) => {
    setLoading(true);
    const token = sessionStorage.getItem("jwtToken");

    let url = "https://api.ipaisa.co.in/myapp/auth/getUtility?";
    if (searchQuery.trim()) {
      url += `searchBy=${searchQuery}&`;
    }
    if (fromDate) {
      url += `fromDate=${fromDate}&`;
    }
    if (toDate) {
      url += `toDate=${toDate}&`;
    }
    url += `page=${page}&size=${pageSize}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (
        response.data.statusCodeValue === 204 ||
        response.data.body.message === "NO_CONTENT"
      ) {
        setFilteredData([]);
        setStatusCodeValue(response.data.statusCodeValue);
        setTotalRecords(response.data.body.totalRecords || 0);
      } else {
        setFilteredData(response.data.body?.data || []);
        setTotalRecords(response.data.body?.totalRecords || 0);
      }
    } catch (error) {
      console.error("Error fetching completed transactions:", error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompleteData(currentPage, fromDate, toDate, searchQuery, pageSize);
  }, [currentPage, pageSize]);

  const handleClear = () => {
    setFromDate("");
    setSearchQuery("");
    setToDate("");
    setCurrentPage(0);
    fetchCompleteData(0, "", "", "", pageSize);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value - 1);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(0);
  };

  const formatDate = (dateTimeString) => {
    if (!dateTimeString) return "";
    const [datePart] = dateTimeString.split("T");
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  };

  if (loading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        style={{ height: "100vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
      <Grid
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1%",
          justifyContent: "start",
        }}
      >
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "14px" }}
        >
          Reports
        </Typography>
        <PartnerArrowIcon />
        <Typography
          color="textSecondary"
          style={{ fontFamily: "sans-serif", fontSize: "14px" }}
        >
          Utility Bill Pay Report
        </Typography>
      </Grid>

      {/* <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          marginTop: "1%",
          flexWrap: "wrap",
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ marginBottom: { xs: "1rem", sm: "0" } }}
        >
          <Typography
            style={{
              fontWeight: "bold",
              fontFamily: "sans-serif",
              fontSize: "19px",
              color: "#343C6A",
            }}
          >
            Utility Bill Pay Report
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "flex-end" },
            gap: "1rem",
            flexWrap: "wrap",
          }}
        >
          <Button
            style={{
              textTransform: "none",
              backgroundColor: "#027BFE",
              color: "#FFFFFF",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 16px",
              fontFamily: "sans-serif",
              fontSize: "14px",
              whiteSpace: "nowrap",
              gap: "5%",
              height: "40px",
            }}
            onClick={handleDownloadPDF}
          >
            <DownloadIcon />
            Download PDF File
          </Button>

          <CSVLink
            data={handleDownloadCSV()}
            filename={"pay_in_report.csv"}
            target="_blank"
          >
            <Button
              style={{
                textTransform: "none",
                backgroundColor: "#027BFE",
                color: "#FFFFFF",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 16px",
                fontFamily: "sans-serif",
                fontSize: "14px",
                whiteSpace: "nowrap",
                gap: "5%",
                height: "40px",
              }}
            >
              <DownloadIcon />
              Download CSV File
            </Button>
          </CSVLink>
        </Grid>
      </Grid> */}

      <Paper
        style={{
          padding: "2rem",
          borderRadius: "8px",
          marginTop: "2%",
          boxShadow: "none",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              marginTop: "1%",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "1rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flex: 1,
                maxWidth: { xs: "100%", sm: "50%" },
                borderRadius: "10px",
                border: "1px solid rgba(0, 0, 0, 0.3)",
                padding: "0.5rem",
              }}
            >
              <InputBase
                fullWidth
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchInput}
              />
              <IconButton color="primary" onClick={handleSearch}>
                <SearchIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
              }}
            >
              <Button
                onClick={handleSearch}
                sx={{
                  backgroundColor: "#027BFE",
                  color: "white",
                  height: "56px",
                  textTransform: "none",
                  borderRadius: "10px",
                  width: { xs: "100%", sm: "120px" },
                }}
              >
                Search
              </Button>
              <Button
                onClick={handleClear}
                sx={{
                  backgroundColor: "gray",
                  color: "white",
                  height: "56px",
                  textTransform: "none",
                  borderRadius: "10px",
                  width: { xs: "100%", sm: "120px" },
                }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "100vh" }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        <Grid style={{ marginTop: "3%" }}>
          <TableContainer
            component={Paper}
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              borderRadius: "12px 12px 0px 0px",
              boxShadow: "none",
            }}
          >
            <Table>
              <TableHead style={{ backgroundColor: "#027BFE", color: "white" }}>
                <TableRow>
                  <TableCell style={{ color: "white" }} align="start">
                    Sr. No.
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Txn ID
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Pool Reference Id
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Date
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Opening Balance
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Amount
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Closing Balance
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Service Type
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    Biller Name
                  </TableCell>
                  <TableCell style={{ color: "white" }} align="start">
                    User Id
                  </TableCell>

                  <TableCell style={{ color: "white" }} align="start">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <TableRow
                      key={row.id}
                      className={`${classes.tableRow} ${
                        index % 2 === 0 ? classes.oddRow : classes.evenRow
                      }`}
                    >
                      <TableCell>
                        {currentPage * pageSize + index + 1}
                      </TableCell>
                      <TableCell align="start">{row.externalRef}</TableCell>
                      <TableCell align="start">
                        {row.poolReferenceId || "N/A"}
                      </TableCell>
                      <TableCell align="start">
                        {new Date(row.timestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="start">
                        {row.walletOpeningBal?.toFixed(2) || "N/A"}
                      </TableCell>
                      <TableCell align="start">
                        {row.amount?.toFixed(2)}
                      </TableCell>
                      <TableCell align="start">
                        {row.walletClosingBal?.toFixed(2)}
                      </TableCell>
                      <TableCell align="start">{row.billerName}</TableCell>
                      <TableCell align="start">{row.service}</TableCell>
                      <TableCell align="start">{row.userId}</TableCell>
                      <TableCell
                        align="start"
                        style={{
                          color:
                            row.status.toLowerCase() ===
                            "transaction successful"
                              ? "green"
                              : row.status.toLowerCase() === "refund"
                                ? "ad"
                                : row.status.toLowerCase() === "pending"
                                  ? "orange"
                                  : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {row.status.toLowerCase() === "transaction successful"
                          ? "Successful"
                          : row.status.toLowerCase() === "refund"
                            ? "Refund"
                            : row.status.toLowerCase() === "pending"
                              ? "Pending"
                              : "Failed"}
                      </TableCell>
                      {/* <TableCell align="center">
                        <DownloadIcon1
                          onClick={() => handleDownloadInvoice(row)}
                          style={{ cursor: "pointer" }}
                        />
                      </TableCell> */}
                      <Grid
                        style={{
                          position: "absolute",
                          left: "-9999px",
                          top: "0px",
                        }}
                      >
                        {/* <Grid id={`receipt-${row.txnid}`}>
                          <PayinReportInvoice data={row} />
                        </Grid> */}
                      </Grid>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No Data Available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            style={{ marginTop: "1%" }}
          >
            <Grid item>
              <Select
                variant="outlined"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
              <Typography display="inline" style={{ marginLeft: 8 }}>
                Showing {currentPage * pageSize + 1} to{" "}
                {Math.min((currentPage + 1) * pageSize, totalRecords)} out of{" "}
                {totalRecords} records
              </Typography>
            </Grid>
            <Grid item>
              <Pagination
                count={Math.ceil(totalRecords / pageSize)}
                color="primary"
                page={currentPage + 1}
                onChange={handlePageChange}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Utility;
